import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  AlertTriangle,
  ChevronDown,
  X,
  UserRound,
  MessageCircle,
  Ticket,
  Handshake,
  ListTodo,
  CalendarClock,
  Contact,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AssignAgentMenu } from '@/components/reusable/AssignAgentMenu';
import { getAvatarColorFromId, getAssigneeInitial } from '@/utils/conversationListHelpers';
import { buildReassignPlan, bucketKey, type ReassignBucket } from '@/lib/team/reassignTargets';
import { TeamService, type MemberAssignmentSummary } from '@/lib/api/services/teamService';
import { useAgentsById } from '@/hooks/useAssignableAgents';
import { CHANNEL_GLYPH } from '@/components/dashboard/theme/ChannelAvatar';
import { cn } from '@/lib/utils';

export type DeactivationChoice =
  | { mode: 'keep' }
  | { mode: 'reassign'; targetUserId: string; assignments?: Array<{ workspaceId: string | null; targetUserId: string | null }> }
  | { mode: 'unassign' };

const COUNT_LABELS: Array<[keyof MemberAssignmentSummary['counts'], string, string]> = [
  ['conversations', 'WhatsApp conversation', 'WhatsApp conversations'],
  ['social_threads', 'Instagram / Facebook chat', 'Instagram / Facebook chats'],
  ['contacts', 'contact they own', 'contacts they own'],
  ['deals', 'deal', 'deals'],
  ['tickets', 'ticket', 'tickets'],
  ['tasks', 'open task', 'open tasks'],
  ['activities', 'planned CRM activity', 'planned CRM activities'],
];

type CountKey = keyof MemberAssignmentSummary['counts'];
type CountItem = { key: CountKey; text: string };

function describeCountItems(counts: Partial<Record<CountKey, number | null>> | null | undefined): CountItem[] {
  if (!counts) return [];
  const items: CountItem[] = [];
  for (const [key, one, many] of COUNT_LABELS) {
    const n = counts[key];
    if (typeof n === 'number' && n > 0) items.push({ key, text: `${n} ${n === 1 ? one : many}` });
  }
  return items;
}

function describeCounts(counts: Partial<Record<CountKey, number | null>> | null | undefined): string[] {
  return describeCountItems(counts).map((i) => i.text);
}

export function describeAssignmentCounts(summary: MemberAssignmentSummary | null | undefined): string[] {
  if (!summary) return [];
  return describeCounts(summary.counts);
}

export function describeAssignmentsByWorkspace(
  summary: MemberAssignmentSummary | null | undefined,
): Array<{ key: string; label: string; lines: string[] }> | null {
  const groups = workspaceItemGroups(summary);
  return groups ? groups.map((g) => ({ key: g.key, label: g.label, lines: g.items.map((i) => i.text) })) : null;
}

const bucketLabel = (b: Pick<ReassignBucket, 'workspaceId' | 'name'>) =>
  b.workspaceId ? b.name || 'Another workspace' : 'Work without a workspace';

const COUNT_ICON: Record<CountKey, React.ReactNode> = {
  conversations: <span className="block h-3.5 w-3.5">{CHANNEL_GLYPH.whatsapp}</span>,
  social_threads: <MessageCircle className="h-3.5 w-3.5" />,
  contacts: <Contact className="h-3.5 w-3.5" />,
  deals: <Handshake className="h-3.5 w-3.5" />,
  tickets: <Ticket className="h-3.5 w-3.5" />,
  tasks: <ListTodo className="h-3.5 w-3.5" />,
  activities: <CalendarClock className="h-3.5 w-3.5" />,
};

function WorkChips({ items }: { items: CountItem[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((i) => (
        <span
          key={i.key}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#F2F4F7] px-2 py-1 text-[12px] font-medium text-[#344054]"
          data-testid={`deactivate-member-chip-${i.key}`}
        >
          <span className="shrink-0 text-[#475467]">{COUNT_ICON[i.key]}</span>
          <span>{i.text}</span>
        </span>
      ))}
    </div>
  );
}

function workspaceItemGroups(summary: MemberAssignmentSummary | null | undefined) {
  if (!summary || summary.scopes_incomplete) return null;
  const groups = (summary.work_by_workspace ?? [])
    .filter((b) => (b?.total ?? 0) > 0)
    .map((b) => ({
      key: bucketKey(b.workspace_id),
      label: b.workspace_id ? b.name || 'Another workspace' : 'No workspace',
      scoped: !!b.workspace_id,
      items: describeCountItems(b.counts),
    }))
    .filter((g) => g.items.length > 0)
    .sort((x, y) => Number(!x.scoped) - Number(!y.scoped) || x.label.localeCompare(y.label, undefined, { sensitivity: 'base' }));
  return groups.length > 0 ? groups : null;
}

function ChoiceCard({
  value,
  selected,
  title,
  description,
  children,
}: {
  value: DeactivationChoice['mode'];
  selected: boolean;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  const id = `dm-${value}`;
  return (
    <div
      className={cn(
        'rounded-xl border px-3 py-3 transition-colors',
        selected ? 'border-[#D1FADF] bg-brand-subtle' : 'border-transparent',
      )}
      data-testid={`deactivate-member-choice-${value}`}
      data-selected={selected ? 'true' : 'false'}
    >
      <div className="flex items-start gap-3">
        <RadioGroupItem value={value} id={id} className="mt-0.5" />
        <div className="w-full min-w-0">
          <Label htmlFor={id} className="cursor-pointer font-normal leading-snug">
            <span className="block text-[14px] font-semibold text-[#101828]">{title}</span>
            <span className="mt-0.5 block text-[13px] text-[#667085]">{description}</span>
          </Label>
          {children}
        </div>
      </div>
    </div>
  );
}

interface DeactivateMemberDialogProps {
  open: boolean;
  memberId: string;
  memberName: string;
  purpose?: 'deactivate' | 'reassign';
  confirming?: boolean;
  onCancel: () => void;
  onConfirm: (choice: DeactivationChoice) => void | Promise<void>;
}

export function DeactivateMemberDialog({
  open,
  memberId,
  memberName,
  purpose = 'deactivate',
  confirming = false,
  onCancel,
  onConfirm,
}: DeactivateMemberDialogProps) {
  const [mode, setMode] = React.useState<DeactivationChoice['mode']>(purpose === 'reassign' ? 'reassign' : 'keep');
  const [targetUserId, setTargetUserId] = React.useState<string>('');

  const { data: summary, isLoading, isError } = useQuery({
    queryKey: ['team-assignment-summary', memberId],
    queryFn: async () => {
      const res = await TeamService.getAssignmentSummary(memberId);
      return (res as { data?: MemberAssignmentSummary } | undefined)?.data ?? null;
    },
    enabled: open && !!memberId,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });

  const directory = useAgentsById();
  const plan = React.useMemo(
    () => buildReassignPlan(directory, memberId, summary),
    [directory, memberId, summary],
  );
  const candidates = plan.candidates;
  const selectedTarget = candidates.find((a) => a.id === targetUserId) || null;
  const [restTargets, setRestTargets] = React.useState<Record<string, string | null>>({});

  React.useEffect(() => {
    if (targetUserId && !candidates.some((a) => a.id === targetUserId)) setTargetUserId('');
  }, [candidates, targetUserId]);

  React.useEffect(() => {
    setRestTargets({});
  }, [targetUserId]);

  const uncoveredBuckets = React.useMemo(
    () => (plan.split && targetUserId
      ? plan.buckets.filter((b) => !b.candidates.some((c) => c.id === targetUserId))
      : []),
    [plan, targetUserId],
  );

  React.useEffect(() => {
    setRestTargets((prev) => {
      let changed = false;
      const next: Record<string, string | null> = {};
      for (const b of uncoveredBuckets) {
        const chosen = prev[b.key] ?? null;
        const stillValid = chosen === null || b.candidates.some((c) => c.id === chosen);
        next[b.key] = stillValid ? chosen : null;
        if (!stillValid) changed = true;
      }
      if (Object.keys(prev).length !== Object.keys(next).length) changed = true;
      return changed ? next : prev;
    });
  }, [uncoveredBuckets]);

  const candidateOptions = React.useMemo(() => {
    const scoped = plan.buckets.filter((b) => !!b.workspaceId);
    return candidates.map((a) => {
      let helper: string | undefined;
      if (plan.split) {
        const covered = scoped.filter((b) => b.candidates.some((c) => c.id === a.id));
        const role = String(a.role ?? '').trim().toUpperCase();
        const where = covered.length === scoped.length
          ? 'All their workspaces'
          : covered.map((b) => bucketLabel(b)).join(', ');
        helper = role ? `${role} · ${where}` : where;
      }
      return { id: a.id, name: a.name, email: a.email, role: a.role, helper };
    });
  }, [candidates, plan]);

  const lines = describeAssignmentCounts(summary);
  const flatItems = describeCountItems(summary?.counts);
  const workspaceGroups = workspaceItemGroups(summary);
  const hasWork = lines.length > 0;
  const showChoices = hasWork || purpose === 'reassign';
  const name = memberName || 'this member';

  const canConfirm =
    !confirming &&
    !isLoading &&
    (mode !== 'reassign' || !!targetUserId) &&
    (purpose === 'deactivate' || hasWork);

  const confirm = () => {
    if (mode === 'reassign' && uncoveredBuckets.length > 0) {
      const uncovered = new Set(uncoveredBuckets.map((b) => b.key));
      onConfirm({
        mode: 'reassign',
        targetUserId,
        assignments: plan.buckets.map((b) => ({
          workspaceId: b.workspaceId,
          targetUserId: uncovered.has(b.key) ? restTargets[b.key] ?? null : targetUserId,
        })),
      });
    } else if (mode === 'reassign') onConfirm({ mode: 'reassign', targetUserId });
    else if (mode === 'unassign') onConfirm({ mode: 'unassign' });
    else onConfirm({ mode: 'keep' });
  };

  const confirmLabel =
    purpose === 'reassign'
      ? mode === 'unassign' ? 'Unassign' : 'Reassign'
      : 'Inactivate';

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next && !confirming) onCancel(); }}>
      <AlertDialogContent className="max-w-[520px] gap-0 p-0" data-testid="deactivate-member-dialog">
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-md p-1 text-[#667085] transition-colors hover:bg-[#F2F4F7] hover:text-[#101828] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1EB677]/30 disabled:pointer-events-none disabled:opacity-50"
          data-testid="deactivate-member-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="max-h-[80vh] overflow-y-auto px-6 pb-2 pt-6">
          <AlertDialogHeader className="space-y-1.5 pr-8 text-left">
            <AlertDialogTitle className="text-[20px] font-semibold text-[#101828]">
              {purpose === 'reassign' ? `Reassign ${name}'s work` : 'Inactivate team member?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-[14px] text-[#667085]">
                {purpose === 'deactivate' && (
                  <p>They'll lose access to WeNext immediately. Their account and work will remain in your workspace.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-5 space-y-5 text-sm">
            {isLoading ? (
              <div className="flex items-center gap-2 text-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking what is assigned to them…
              </div>
            ) : isError || !summary ? (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>We couldn't check what is assigned to them. Anything they own will stay assigned to them.</span>
              </div>
            ) : hasWork ? (
              <div className="rounded-xl border border-[#EAECF0] bg-[#F9FAFB] p-4 text-foreground" data-testid="deactivate-member-counts">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#475467]">Assigned work</p>
                {workspaceGroups ? (
                  <div className="divide-y divide-[#EAECF0]">
                    {workspaceGroups.map((g) => (
                      <div key={g.key} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0" data-testid={`deactivate-member-counts-${g.key}`}>
                        <span
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold"
                          style={getAvatarColorFromId(g.key)}
                          aria-hidden
                        >
                          {getAssigneeInitial(g.label, g.key)}
                        </span>
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <p className="truncate text-[14px] font-semibold text-[#101828]">{g.label}</p>
                          <WorkChips items={g.items} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <WorkChips items={flatItems} />
                )}
                {summary.incomplete && (
                  <p className="mt-3 text-xs text-muted-foreground">Some counts couldn't be loaded and are left out.</p>
                )}
              </div>
            ) : (
              <p className="text-foreground">Nothing is currently assigned to them.</p>
            )}

            {showChoices && !isLoading && summary && hasWork && (
              <div className="space-y-2">
                <p className="text-[15px] font-semibold text-[#101828]">What happens to their work?</p>
                <RadioGroup
                  value={mode}
                  onValueChange={(v) => setMode(v as DeactivationChoice['mode'])}
                  className="gap-1 text-foreground"
                  data-testid="deactivate-member-choices"
                >
                  {purpose === 'deactivate' && (
                    <ChoiceCard
                      value="keep"
                      selected={mode === 'keep'}
                      title="Keep assigned"
                      description="Their work will stay assigned to them. You can reassign it later."
                    />
                  )}
                  <ChoiceCard
                    value="reassign"
                    selected={mode === 'reassign'}
                    title="Reassign to another team member"
                    description={mode === 'reassign' && candidates.length === 0
                      ? plan.split
                        ? 'No active teammate is in any of the workspaces their work is in. Move it to Unassigned, or give a teammate access first.'
                        : 'No active teammate has access to all of the workspaces their work is in. Move it to Unassigned, or give a teammate access first.'
                      : 'Choose a team member to take over their assigned work.'}
                  >
                    {mode === 'reassign' && (
                      <AssignAgentMenu
                        currentAgentId={targetUserId || null}
                        agents={candidateOptions}
                        onAssign={(id) => setTargetUserId(id || '')}
                        showUnassigned={false}
                        emptyText={plan.split ? 'No active teammate is in any of their workspaces' : 'No active teammate can access all of their workspaces'}
                        align="start"
                        width={320}
                        data-testid="deactivate-member-target-menu"
                      >
                        <button
                          type="button"
                          className="mt-3 flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-[#D0D5DD] bg-white px-3 text-left text-[14px] text-[#101828] hover:bg-[#F9FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1EB677]/30"
                          data-testid="deactivate-member-target"
                        >
                          {selectedTarget ? (
                            <span className="flex min-w-0 items-center gap-2">
                              <span
                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                                style={getAvatarColorFromId(selectedTarget.id)}
                              >
                                {getAssigneeInitial(selectedTarget.name || null, selectedTarget.id)}
                              </span>
                              <span className="truncate">{selectedTarget.name || selectedTarget.email}</span>
                            </span>
                          ) : (
                            <span className="flex min-w-0 items-center gap-2 text-[#667085]">
                              <UserRound className="h-4 w-4 shrink-0" />
                              Select a team member
                            </span>
                          )}
                          <ChevronDown className="h-4 w-4 shrink-0 text-[#667085]" />
                        </button>
                      </AssignAgentMenu>
                    )}
                    {mode === 'reassign' && selectedTarget && uncoveredBuckets.length > 0 && (
                      <div className="mt-3 space-y-2 rounded-lg border border-[#EAECF0] bg-white p-3" data-testid="deactivate-member-rest">
                        <p className="text-xs text-foreground">
                          {selectedTarget.name || selectedTarget.email} isn't in {uncoveredBuckets.map((b) => bucketLabel(b)).join(', ')}. Choose who takes that work:
                        </p>
                        {uncoveredBuckets.map((b) => {
                          const chosenId = restTargets[b.key] ?? null;
                          const chosen = chosenId ? b.candidates.find((c) => c.id === chosenId) || null : null;
                          return (
                            <div key={b.key} className="space-y-1" data-testid={`deactivate-member-rest-${b.key}`}>
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">{bucketLabel(b)}</span>
                                {describeCounts(b.counts).length > 0 && <> · {describeCounts(b.counts).join(', ')}</>}
                              </p>
                              <AssignAgentMenu
                                currentAgentId={chosenId}
                                agents={b.candidates.map((a) => ({ id: a.id, name: a.name, email: a.email, role: a.role }))}
                                onAssign={(id) => setRestTargets((prev) => ({ ...prev, [b.key]: id || null }))}
                                showUnassigned
                                emptyText={`No active teammate is in ${bucketLabel(b)}`}
                                align="start"
                                width={300}
                                data-testid={`deactivate-member-rest-menu-${b.key}`}
                              >
                                <button
                                  type="button"
                                  className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-[#D0D5DD] bg-white px-3 text-left text-sm text-[#101828] hover:bg-[#F9FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1EB677]/30"
                                  data-testid={`deactivate-member-rest-target-${b.key}`}
                                >
                                  {chosen ? (
                                    <span className="flex min-w-0 items-center gap-2">
                                      <span
                                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                                        style={getAvatarColorFromId(chosen.id)}
                                      >
                                        {getAssigneeInitial(chosen.name || null, chosen.id)}
                                      </span>
                                      <span className="truncate">{chosen.name || chosen.email}</span>
                                    </span>
                                  ) : (
                                    <span className="text-[#667085]">Move to Unassigned</span>
                                  )}
                                  <ChevronDown className="h-4 w-4 shrink-0 text-[#667085]" />
                                </button>
                              </AssignAgentMenu>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ChoiceCard>
                  <ChoiceCard
                    value="unassign"
                    selected={mode === 'unassign'}
                    title="Move to Unassigned"
                    description="Their work will move to Unassigned so a teammate can pick it up."
                  />
                </RadioGroup>
              </div>
            )}
          </div>
        </div>
        <AlertDialogFooter className="mx-6 mt-4 gap-2 border-t border-[#EAECF0] py-4">
          <AlertDialogCancel disabled={confirming} className="mt-0">Cancel</AlertDialogCancel>
          <Button
            variant={purpose === 'deactivate' ? 'destructive' : 'default'}
            disabled={!canConfirm}
            onClick={confirm}
            data-testid="deactivate-member-confirm"
          >
            {confirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
